import { localized } from '@platform/core/lib/localized';
import type { IModuleDefinition, IModuleSummary, Lang } from '@platform/core/types';
import type { IPartyCalendarType } from './IPartyCalendarType';
import { calendarDateKeys } from './partyCalendarModel';
import { partyHue } from './partyColors';

/** Категорія модулів календаря партії (прототип v4 pcal: host.cat === 'Party & Group'). */
export const PARTY_CATEGORY = 'Party & Group';

/**
 * Модулі категорії «Party & Group», встановлені в просторі: спершу в порядку групи верхнього меню (`order` — порядок модулів прототипу:
 * Учасники, З’їзди й засідання, Програма партії, Кампанії, Фінанси, База знань партії, Партійна робота; ярлики «модуль/тип» пропускаються),
 * далі решта — у порядку каталогу. Чиста функція.
 */
export function partyModuleKeys(modules: readonly IModuleSummary[], order: readonly string[]): string[] {
  const party = modules.filter((m) => m.installed && m.category === PARTY_CATEGORY).map((m) => m.key);
  const first = order.filter((k) => !k.includes('/') && party.includes(k));
  return [...first, ...party.filter((k) => !first.includes(k))];
}

/**
 * Типи календаря: реалізовані типи записів кожного модуля (у порядку модулів, усередині — у порядку визначення: головний, далі дочірні) —
 * «модулі» прототипу. Підпис головного типу — назва модуля, дочірнього — назва для показу або назва типу. Відтінок — за місцем типу серед
 * усіх (і тих, що без подій), як у прототипі. Чиста функція.
 */
export function partyTypes(definitions: readonly IModuleDefinition[], keys: readonly string[], lang: Lang): IPartyCalendarType[] {
  const byKey = new Map(definitions.map((d) => [d.module.key, d]));
  const out: IPartyCalendarType[] = [];
  for (const key of keys) {
    const definition = byKey.get(key);
    for (const rt of definition?.recordTypes ?? []) {
      if (rt.status === 'designed') continue;
      const name = rt.main ? definition?.module.displayName ?? definition?.module.name : rt.displayName ?? rt.name;
      out.push({
        key: rt.key, moduleKey: key, label: localized(name, lang) || rt.key, hue: partyHue(out.length), dateKeys: calendarDateKeys(rt.fields),
        statuses: Object.fromEntries(rt.statuses.list.map((s) => [s.value, localized(s.label, lang) || s.value])),
      });
    }
  }
  return out;
}
